import { useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router'

import { Icon } from '../components/Icon'
import { ErrorBox, Section } from '../components/ui'
import { api, invalidate, useAction, useApi, usePoll } from '../lib/api'
import { useI18n, type MessageKey } from '../lib/i18n'
import { useSession } from '../lib/session'
import { ALERT_KINDS, EDITOR_KINDS, MODERATOR_KINDS, NOTIFICATION_KINDS, OPT_IN_KINDS, type Inbox, type Me, type NotificationItem } from '../lib/types'

const BELL_POLL_MS = 60_000
const IN_APP_ONLY = new Set<NotificationItem['kind']>(['upload_finished', 'ops_resolved', 'forum_removed', 'label_proposal_decided', 'site_role', 'site_role_ended'])

function useText(): (item: NotificationItem) => string {
  const { t } = useI18n()
  return (item) => {
    const vars: Record<string, string> = Object.fromEntries(Object.entries(item.payload).map(([key, value]) => [key, String(value)]))
    // A removed forum post names its reason in words (FOR-12).
    if (item.kind === 'forum_removed') vars.reason = t(`flag_${vars.reason}` as MessageKey)
    // A decided label proposal says how in words (LBL-02).
    if (item.kind === 'label_proposal_decided') vars.decision = t(`proposal_${vars.decision}` as MessageKey)
    // A site role given or changed by someone else names the role in words (NTF-01).
    if (item.kind === 'site_role') vars.role = t(`role_${vars.role}` as MessageKey)
    return t(`notice_${item.kind}` as MessageKey, vars)
  }
}

/** The navigation's link to the notifications, with the unread count (NTF-01). */
export function NotificationBell({ collapsed }: { collapsed: boolean }) {
  const { t } = useI18n()
  const inbox = useApi<Inbox>('/notifications')
  usePoll(true, inbox.poll, BELL_POLL_MS)
  const unread = inbox.data?.unread ?? 0
  return (
    <NavLink to="/notifications" className="nav-item" title={collapsed ? t('notifications') : undefined}>
      <Icon name="bell" />
      <span className="nav-label">{t('notifications')}</span>
      {unread > 0 && (
        <span className="nav-count">
          {unread}
          <span className="visually-hidden"> {t('unread')}</span>
        </span>
      )}
    </NavLink>
  )
}

/** Recent notifications; opening one marks it read (NTF-01). */
export function NotificationsPage() {
  const { t, lang } = useI18n()
  const navigate = useNavigate()
  const inbox = useApi<Inbox>('/notifications')
  const text = useText()
  const { busy, error, run } = useAction()
  // Marking it read is bookkeeping: the notice opens even when that fails, and stays unread. The
  // navigation's count follows, as everywhere the notifications show (`invalidate`).
  const open = (item: NotificationItem) => {
    if (!item.read_at) {
      void api('/notifications/read', { json: { ids: [item.id] } }).then(() => invalidate('/notifications'), () => undefined)
    }
    void navigate(item.link)
  }
  const readAll = () =>
    run(async () => {
      await api('/notifications/read', { json: {} })
      invalidate('/notifications')
    })
  return (
    <>
      <h1>{t('notifications')}</h1>
      <ErrorBox error={inbox.error} retry={inbox.reload} />
      {inbox.data && inbox.data.unread > 0 && (
        <p>
          <button type="button" className="secondary" disabled={busy} onClick={() => void readAll()}>
            {t('markAllRead')}
          </button>
        </p>
      )}
      <ErrorBox error={error} retry={() => void readAll()} />
      {inbox.data?.items.length === 0 && <p className="muted">{t('none')}</p>}
      <ul className="list notifications">
        {inbox.data?.items.map((item) => (
          <li key={item.id} className={item.read_at ? 'read' : 'unread'}>
            <button type="button" className="link" onClick={() => open(item)}>
              {text(item)}
            </button>
            <small className="muted push">{new Date(item.created_at).toLocaleString(lang)}</small>
          </li>
        ))}
      </ul>
      <EmailSettings />
    </>
  )
}

const OPT_IN = new Set<string>(OPT_IN_KINDS)

type EmailChoice = Pick<Me, 'email_opt_out' | 'email_opt_in'>

/**
 * Which notifications also come by e-mail (NTF-02): most unless switched off, forum replies only if switched on (FOR-10).
 * A change shows at once; the saves go one after another, each with all the choices made by then, so quick changes all stay.
 */
function EmailSettings() {
  const { t } = useI18n()
  const { me, reload } = useSession()
  const [choice, setChoice] = useState<EmailChoice>(() => ({ email_opt_out: me.email_opt_out, email_opt_in: me.email_opt_in }))
  const latest = useRef(choice)
  const saving = useRef(Promise.resolve())
  const { busy, error, run } = useAction()
  const save = () => {
    saving.current = saving.current.then(() =>
      run(async () => {
        await api('/me/notifications', { method: 'PUT', json: latest.current })
        reload()
      }),
    )
  }
  const toggle = (kind: string, email: boolean) => {
    const without = (kinds: string[]) => kinds.filter((entry) => entry !== kind)
    const { email_opt_in, email_opt_out } = latest.current
    latest.current = {
      email_opt_in: OPT_IN.has(kind) && email ? [...email_opt_in, kind] : without(email_opt_in),
      email_opt_out: OPT_IN.has(kind) || email ? without(email_opt_out) : [...email_opt_out, kind],
    }
    setChoice(latest.current)
    save()
  }
  const emailed = (kind: string) => (OPT_IN.has(kind) ? choice.email_opt_in.includes(kind) : !choice.email_opt_out.includes(kind))
  const moderates = me.is_platform_admin || me.forum_moderator
  const kinds = [
    ...NOTIFICATION_KINDS,
    ...OPT_IN_KINDS,
    ...(moderates ? MODERATOR_KINDS : []),
    ...(me.catalog_editor ? EDITOR_KINDS : []),
    ...(me.is_platform_admin ? ALERT_KINDS : []),
  ]
  return (
    <Section title={t('emailNotifications')} actions={<span className="muted small" role="status">{busy && t('save_saving')}</span>}>
      <p className="muted">{t('emailNotificationsIntro')}</p>
      <ErrorBox error={error} retry={save} />
      {kinds.filter((kind) => !IN_APP_ONLY.has(kind)).map((kind) => (
        <label key={kind} className="check">
          <input type="checkbox" checked={emailed(kind)} onChange={(event) => toggle(kind, event.target.checked)} />
          {t(`kind_${kind}` as MessageKey)}
        </label>
      ))}
    </Section>
  )
}
